import {
	ArrowBackRounded,
	ArrowForwardRounded,
	SkipNext,
} from "@mui/icons-material";
import {
	Grid2,
	Grid2Props,
	IconButton,
	Paper,
	TextField,
	Tooltip,
} from "@mui/material";

import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import moment, { Moment } from "moment-timezone";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getError } from "../../../utils";
import { useShiftNavigator } from "../context/ShiftNavigatorProvider";
import { ShiftInfo } from "../models/shift-navigator.model";
import { getShiftByParameters } from "../repositories/ShiftsRepository";

export type ShiftNavigatorDirection = "Previous" | "Next" | "Last";

export const ShiftDayNavigatorControl = ({
	useRouter,
	onShiftChange,
	preventInit,
	withPaper = true,
	margin = "1rem 1rem 1rem 1rem",
	assetId,
	...props
}: {
	useRouter?: boolean;
	onShiftChange?: (shiftId: number) => void;
	preventInit?: boolean;
	withPaper?: boolean;
	margin?: string;
	assetId?: number;
} & Grid2Props) => {
	const { shiftInfo, initShiftNavigator, moveShift, getShiftDataFromAPI } =
		useShiftNavigator();

	const [shiftInfoCopy, setShiftInfoCopy] = useState(shiftInfo);
	const [error, setError] = useState<string>("");

	const { shiftId } = useParams();
	const firstRender = useRef(true);

	const updateHistory = (shiftId: string) => {
		const paths = location.pathname.split("/");
		window.history.replaceState(null, "", `/${paths[1]}` + `/${shiftId}`);
	};

	if (shiftInfo !== null && shiftInfo.CurrentShiftId) {
		if (useRouter) {
			updateHistory(shiftInfo?.CurrentShiftId?.toString());
		}
	}

	useEffect(() => {
		const fn = async () => {
			if (shiftInfo?.CurrentEnd && shiftInfo.CurrentEnd < new Date()) {
				try {
					const resp = await getShiftByParameters({
						productionDate: null,
						shiftId: shiftInfo!.CurrentShiftId,
					});
					if (resp.ok) {
						const shift: ShiftInfo = {
							...resp.data,
							CurrentProductionDate: moment
								.utc(resp.data.CurrentProductionDate)
								.toDate(),
							CurrentStart: moment.utc(resp.data.CurrentStart).toDate(),
							CurrentEnd: moment.utc(resp.data.CurrentEnd).toDate(),
							PreviousProductionDate: moment
								.utc(resp.data.PreviousProductionDate)
								.toDate(),
							PreviousStart: moment.utc(resp.data.PreviousStart).toDate(),
							PreviousEnd: moment.utc(resp.data.PreviousEnd).toDate(),
							LastProductionDate: moment
								.utc(resp.data.LastProductionDate)
								.toDate(),
							LastStart: moment.utc(resp.data.LastStart).toDate(),
							LastEnd: moment.utc(resp.data.LastEnd).toDate(),
						};
						setShiftInfoCopy(shift);
					}
				} catch (e: any) {
					setError(getError(e));
				}
			}
		};
		const interval = setInterval(fn, 1000 * 60);
		return () => clearInterval(interval);
	}, [shiftInfo]);

	useEffect(() => {
		setShiftInfoCopy(shiftInfo);
	}, [shiftInfo]);

	useEffect(() => {
		if (firstRender.current) {
			firstRender.current = false;

			if (preventInit) {
				return;
			}

			if (useRouter && shiftId) {
				getShiftDataFromAPI(null, Number(shiftId), assetId).then(() => {
					onShiftChange && onShiftChange(Number(shiftId));
				});
			} else {
				initShiftNavigator(assetId).then(() => {
					onShiftChange && onShiftChange(Number(shiftId));
				});
			}
		}
	}, []);

	const gridContent = (
		<Grid2
			container
			justifyContent={"flex-start"}
			alignItems={"center"}
			spacing={1}
			px={1}
			{...props}
		>
			<Grid2 size="auto">
				<Tooltip title={"Previous Shift"}>
					<IconButton
						aria-label="Previous Shift"
						color={"primary"}
						disabled={!shiftInfo?.PreviousShiftId}
						onClick={() => {
							moveShift("Previous", assetId).then(() => {});
						}}
						size="small"
					>
						<ArrowBackRounded />
					</IconButton>
				</Tooltip>
			</Grid2>
			<Grid2 size="auto">
				<MuiDatePicker
					label="Current Date"
					timezone={"UTC"}
					format="MM/DD/YYYY"
					maxDate={moment(shiftInfo?.LastProductionDate) || moment()}
					closeOnSelect
					formatDensity={"dense"}
					value={moment(shiftInfo?.CurrentProductionDate)}
					onChange={(date: Moment | null) => {
						if (date) {
							getShiftDataFromAPI(date.toDate(), 0, assetId).then(() => {});
						}
					}}
					slots={{
						textField: TextField,
					}}
					slotProps={{
						textField: {
							variant: "outlined",
							size: "small",
							inputProps: {
								style: {
									textAlign: "center",
									maxWidth: 100,
								},
							},
						},
					}}
				/>
			</Grid2>
			<Grid2 size="auto" maxWidth={100}>
				<TextField
					label={"Shift - Crew"}
					value={`${shiftInfo?.CurrentShift || ""} - ${
						shiftInfo?.CurrentCrew || ""
					}`}
					placeholder={"Shift"}
					aria-readonly
					variant="outlined"
					size="small"
				/>
			</Grid2>
			<Grid2 size="auto">
				<Tooltip title={"Next Shift"}>
					<IconButton
						aria-label="next"
						color={"primary"}
						disabled={!shiftInfoCopy?.NextShiftId}
						onClick={() => {
							moveShift("Next", assetId).then(() => {});
						}}
						size="small"
					>
						<ArrowForwardRounded />
					</IconButton>
				</Tooltip>
			</Grid2>
			<Grid2>
				<Tooltip title={"Last Shift"}>
					<IconButton
						aria-label="delete"
						color={"primary"}
						disabled={
							!shiftInfoCopy?.LastShiftId || !shiftInfoCopy?.NextShiftId
						}
						onClick={() => {
							moveShift("Last", assetId).then(() => {});
						}}
						size="small"
					>
						<SkipNext />
					</IconButton>
				</Tooltip>
			</Grid2>
		</Grid2>
	);

	return withPaper !== false ? <Paper>{gridContent}</Paper> : gridContent;
};
